import { useMemo, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import styled from "styled-components";

import { Button } from "../components/Button.tsx";
import { Stack } from "../components/Stack.tsx";
import { SummaryBox } from "../components/SummaryBox.tsx";
import { VirtualSrtPreview } from "../components/VirtualSrtPreview.tsx";
import { Slider } from "../components/Slider.tsx";
import {
  CloudExportIndicator,
  type CloudExportState,
} from "../components/CloudExportIndicator.tsx";

import {
  audioFileAtom,
  oracleJsonAtom,
  profilesAtom,
  customSpeakersAtom,
  manualOverridesAtom,
  currentProcessingIdAtom,
} from "../../state/atoms.ts";
import {
  finalSegmentsAtom,
  taggedSrtAtom,
} from "../../state/derived.ts";

import {
  downloadTextAsFile,
  suggestTaggedSrtFilename,
} from "../../platform/browser-fs.ts";
import { ApiError, exportTaggedSrt } from "../../platform/api.ts";
import { useNavigateTo, useSyncParams, useRequireMeetingData } from "../routing.ts";

// Default: mostrar hasta 500 segmentos al entrar.
const DEFAULT_MAX_BLOCKS = 500;

// --- Tipos de estado de pantalla ---

type ScreenState =
  | { kind: "ready" }
  | { kind: "incomplete"; missingSpeakers: number[] }
  | { kind: "saving" }
  | { kind: "saved"; filename: string; bytes: number }
  | { kind: "error"; message: string };

// --- Pantalla ---

export function ExportScreen() {
  const navigateTo = useNavigateTo();
  useSyncParams();
  useRequireMeetingData();

  // Inputs
  const finalSegments = useAtomValue(finalSegmentsAtom);
  const taggedSrt = useAtomValue(taggedSrtAtom);
  const profiles = useAtomValue(profilesAtom);
  const overrides = useAtomValue(manualOverridesAtom);
  const currentProcessingId = useAtomValue(currentProcessingIdAtom);

  // Setters para "Procesar otra reunión"
  const setOracle = useSetAtom(oracleJsonAtom);
  const setAudioFile = useSetAtom(audioFileAtom);
  const setProfiles = useSetAtom(profilesAtom);
  const setCustomSpeakers = useSetAtom(customSpeakersAtom);
  const setOverrides = useSetAtom(manualOverridesAtom);

  // Estado local — pantalla
  const [state, setState] = useState<ScreenState>({ kind: "ready" });
  // Estado local — export al bucket (Fase 15).
  const [cloudState, setCloudState] = useState<CloudExportState>({ kind: "idle" });
  const [maxBlocks, setMaxBlocks] = useState<number>(DEFAULT_MAX_BLOCKS);

  // --- Validación: speakers usados en finalSegments deben tener perfil ---
  const validation = useMemo(() => {
    const usedSpeakers = new Set(finalSegments.map((s) => s.speakerIndex));
    const missing: number[] = [];
    for (const sp of usedSpeakers) {
      const p = profiles.get(sp);
      if (!p || p.name.trim() === "") missing.push(sp);
    }
    return { missing };
  }, [finalSegments, profiles]);

  // --- Stats para SummaryBox ---
  const stats = useMemo(() => {
    if (!finalSegments.length) return null;
    const usedSpeakers = new Set(finalSegments.map((s) => s.speakerIndex));
    const usedDetected = [...usedSpeakers].filter((sp) => sp >= 0).length;
    const usedManual = [...usedSpeakers].filter((sp) => sp < 0).length;

    return {
      total: finalSegments.length,
      detected: usedDetected,
      manual: usedManual,
      modified: overrides.size,
    };
  }, [finalSegments, overrides]);

  // --- Upload al bucket (Fase 15) ---
  const performCloudExport = async (srt: string, procId: string) => {
    setCloudState({ kind: "saving" });
    try {
      const result = await exportTaggedSrt(procId, srt);
      setCloudState({ kind: "saved", updatedAt: result.updatedAt });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.message} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "Error de red";
      setCloudState({ kind: "error", message });
    }
  };

  // --- Handlers ---

  const handleSave = async () => {
    if (!taggedSrt) return;
    if (validation.missing.length > 0) {
      setState({ kind: "incomplete", missingSpeakers: validation.missing });
      return;
    }

    setState({ kind: "saving" });

    // 1. Descarga local (síncrona).
    let filename: string;
    let bytes: number;
    try {
      filename = suggestTaggedSrtFilename(null);
      downloadTextAsFile(filename, taggedSrt);
      bytes = new TextEncoder().encode(taggedSrt).length;
    } catch (err) {
      setState({ kind: "error", message: (err as Error).message });
      return;
    }

    setState({ kind: "saved", filename, bytes });

    // 2. Upload al bucket en background.
    if (currentProcessingId !== null) {
      void performCloudExport(taggedSrt, currentProcessingId);
    }
  };

  const handleRetryCloudExport = () => {
    if (!taggedSrt || currentProcessingId === null) return;
    void performCloudExport(taggedSrt, currentProcessingId);
  };

  const handleProcessAnother = () => {
    setOracle(null);
    setAudioFile(null);
    setProfiles(new Map());
    setCustomSpeakers([]);
    setOverrides(new Map());
    navigateTo.meetings();
  };

  const handleBackToPreview = () => setState({ kind: "ready" });

  const goBack = () => currentProcessingId && navigateTo.review(currentProcessingId);

  // --- Render por estado ---

  if (state.kind === "saved") {
    return (
      <Container>
        <SuccessHeader>
          <Title>✓ SRT descargado</Title>
          <Subtitle>El archivo se descargó a la carpeta de descargas de tu navegador.</Subtitle>
        </SuccessHeader>

        <SavedBox>
          <SavedPath>{state.filename}</SavedPath>
          <SavedMeta>
            {state.bytes.toLocaleString()} bytes ·{" "}
            {finalSegments.length.toLocaleString()} segmentos
          </SavedMeta>
        </SavedBox>

        {currentProcessingId !== null && (
          <CloudExportIndicator
            state={cloudState}
            onRetry={handleRetryCloudExport}
          />
        )}

        <Footer>
          <Button $variant="secondary" onClick={handleBackToPreview}>
            Ver en preview
          </Button>
          <Button onClick={handleProcessAnother}>
            Procesar otra reunión
          </Button>
        </Footer>
      </Container>
    );
  }

  if (state.kind === "incomplete") {
    return (
      <Container>
        <Header>
          <Title>Faltan datos para exportar</Title>
          <Subtitle>
            Algunos segmentos están asignados a speakers sin nombre. Volvé a
            Identify para completarlos.
          </Subtitle>
        </Header>

        <ErrorBox>
          <ErrorTitle>Speakers sin perfil completo:</ErrorTitle>
          <ul>
            {state.missingSpeakers.map((sp) => (
              <li key={sp}>
                {sp < 0 ? `Speaker manual ${Math.abs(sp)}` : `Speaker ${sp}`}
              </li>
            ))}
          </ul>
        </ErrorBox>

        <Footer>
          <Button $variant="ghost" onClick={goBack}>
            ← Volver a Review
          </Button>
          <Button onClick={() => currentProcessingId && navigateTo.identify(currentProcessingId)}>
            Ir a Identify
          </Button>
        </Footer>
      </Container>
    );
  }

  // Estado normal: ready / saving / error → mostramos preview
  return (
    <Container>
      <Header>
        <Title>Exportar SRT</Title>
        <Subtitle>Revisa el preview y guarda el archivo.</Subtitle>
      </Header>

      {state.kind === "error" && (
        <ErrorBox>
          <ErrorTitle>Error al guardar:</ErrorTitle>
          <ErrorMessage>{state.message}</ErrorMessage>
        </ErrorBox>
      )}

      {stats && (
        <SummaryBox
          title="Resumen del archivo"
          items={[
            { label: "Segmentos etiquetados", value: stats.total.toLocaleString() },
            {
              label: stats.manual > 0
                ? `Speakers usados (${stats.detected} detectados + ${stats.manual} manuales)`
                : "Speakers usados",
              value: stats.detected + stats.manual,
            },
            {
              label: "Modificados manualmente",
              value: stats.modified,
              tone: stats.modified > 0 ? "success" : "default",
            },
          ]}
        />
      )}

      {taggedSrt && (
        <PreviewSection>
          <PreviewHeader>
            <PreviewTitle>Preview del SRT</PreviewTitle>
          </PreviewHeader>
          <Stack $gap="sm">
            <Slider
              label="Cantidad de segmentos a mostrar"
              min={50}
              max={Math.max(50, finalSegments.length)}
              step={Math.max(1, Math.floor(finalSegments.length / 100))}
              value={Math.min(maxBlocks, finalSegments.length)}
              format={(v) =>
                `${v.toLocaleString()} / ${finalSegments.length.toLocaleString()}`
              }
              onChange={setMaxBlocks}
            />
            <VirtualSrtPreview
              srt={taggedSrt}
              maxBlocks={maxBlocks}
              viewportHeight={480}
            />
          </Stack>
        </PreviewSection>
      )}

      <Footer>
        <Button $variant="ghost" onClick={goBack}>
          ← Volver
        </Button>
        <Button
          onClick={handleSave}
          disabled={!taggedSrt || state.kind === "saving"}
        >
          {state.kind === "saving" ? "Descargando…" : "💾 Descargar SRT"}
        </Button>
      </Footer>
    </Container>
  );
}

// --- Estilos ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;

const Header = styled.div``;

const SuccessHeader = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl} 0;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xxl};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0 0 ${({ theme }) => theme.space.xs} 0;
  color: ${({ theme }) => theme.color.textPrimary};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
`;

const PreviewSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const PreviewTitle = styled.h2`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.textPrimary};
  margin: 0;
`;

const ErrorBox = styled.div`
  background: ${({ theme }) => theme.color.errorMuted};
  border-left: 3px solid ${({ theme }) => theme.color.error};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.space.md};

  ul {
    margin: ${({ theme }) => theme.space.xs} 0 0 ${({ theme }) => theme.space.md};
    padding: 0;
  }
  li {
    color: ${({ theme }) => theme.color.error};
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${({ theme }) => theme.font.size.sm};
  }
`;

const ErrorTitle = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.error};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.color.error};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin-top: ${({ theme }) => theme.space.xs};
  font-family: ${({ theme }) => theme.font.mono};
`;

const SavedBox = styled.div`
  background: ${({ theme }) => theme.color.successMuted};
  border: 1px solid ${({ theme }) => theme.color.success};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const SavedPath = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  word-break: break-all;
`;

const SavedMeta = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;
